import { createRAGFlow, getAllReusableTransforms, loadPipeline } from 'js/actions';
import { resetRAG } from 'js/actions/rag';
import classes from 'js/components/pipelines/ExpandedPipelines/ExpandedPipelines.module.scss';
import { GET_PIPELINES_REQUEST } from 'js/constants/actionTypes';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { RagActions } from 'new/components/flows/types/genai_rag/components/RagActions';
import { RAGAPIUsage } from 'new/components/flows/types/genai_rag/components/RAGAPIUsage';
import { DraftContextProvider } from 'new/components/flows/types/genai_rag/context/Draft/DraftContextProvider';
import Header from 'new/components/header/Header';
import { ROUTE_CONNECT, ROUTE_PROJECTS } from 'new/constants/pathnames';
import { ConnectionLoader } from 'new/elements/ConnectionLoader/ConnectionLoader';
import useEntities from 'new/state/entities/repository/useEntities';
import { Stack } from 'new/elements/Stack';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { useAppContext } from 'new/hooks/useAppContext';
import { ScreenConfig } from 'new/routes/RouterManager';
import React, { useEffect, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import RAGFlowNodes from './RAGFlowNodes';
import { DraftConfirmationDialog } from './components/DraftConfirmationDialog';
import { EditFlowName } from '../../EditFlowName/EditFlowName';
import { assignFlowToProjectIfPresent } from 'new/utils/projectAssignment';
import { useSelector } from 'react-redux';
import { getCurrentProjectId } from 'js/reducers/app/selectors';
import FlowDetailViewContainer from 'js/components/data_flows/cards/FlowDetailViewContainer';
import { FlowDetailActionsMode } from 'new/constants/flowDetailsActionsMode';

const EditRAGFlow = () => {
  const location = useLocation();
  const { setSubHeader, setCurrentSlug } = useAppContext();
  const history = useHistory();
  const { id: rootNodeId } = useParams<any>();
  const dispatch = useAppDispatch();
  const currentProjectId = useSelector(getCurrentProjectId);
  const { data: entities } = useEntities();
  const loading = useIsLoading(GET_PIPELINES_REQUEST);

  // Get selected card ID from Redux state to conditionally render the detail drawer
  const selectedCardID = useSelector((state: any) => state.pipelines?.detailView?.nodeKey);

  const flow = entities.flowNodes[rootNodeId];
  const flowProjectId = flow?.project_id || null;

  useEffect(() => {
    // if /new
    if (/\/new\b/.test(location.pathname)) {
      // create a draft flow
      dispatch(createRAGFlow()).then(async (response) => {
        // redirect to Edit Flow
        const flowId = response.result?.flows?.id?.[0];

        if (flowId) {
          // If started from a Project, assign immediately, but stay on edit page
          try {
            await assignFlowToProjectIfPresent({
              dispatch,
              flowNodeId: flowId,
              currentProjectId,
            });
          } catch (error) {
            console.error('Failed to assign flow to project:', error);
          }

          history.replace(
            `${ROUTE_CONNECT}${ROUTE_PIPELINES}/genai_rag/${flowId}/edit${location.search}`,
          );
        }
      });
    }

    // if /:id/edit, load the flow
    else if (rootNodeId) {
      dispatch(loadPipeline(rootNodeId));
      // If projectId is present, assign flow to project immediately but do not redirect
      (async () => {
        try {
          await assignFlowToProjectIfPresent({
            dispatch,
            flowNodeId: Number(rootNodeId),
            currentProjectId,
          });
        } catch (error) {
          console.error('Failed to assign flow to project:', error);
        }
      })();
    }

    // load query rewriters and context enrichers
    dispatch(
      getAllReusableTransforms(ACLQueryModeTypes.MODE_OWNER.key, {
        resource_type: 'ai_function',
        ai_function_type: 'query_rewriter',
      }),
    );
    dispatch(
      getAllReusableTransforms(ACLQueryModeTypes.MODE_OWNER.key, {
        resource_type: 'ai_function',
        ai_function_type: 'context_enricher',
      }),
    );

    return () => {
      dispatch(resetRAG());
    };
  }, []);

  const subHeaderConfig = useMemo(() => {
    if (!rootNodeId) return null;

    if (flowProjectId) {
      // Flow belongs to a project - use project breadcrumb path
      const projectName = entities.projects[flowProjectId]?.name || null;
      const projectDisplayName = projectName || 'Project ID: ' + String(flowProjectId);
      return {
        slugs: ['integrate', 'projects', projectDisplayName, String(rootNodeId)],
        breadcrumbList: [
          `${ROUTE_CONNECT}`,
          `${ROUTE_PROJECTS}`,
          `${ROUTE_PROJECTS}/${flowProjectId}`,
          '',
        ],
      };
    }
    // Flow doesn't belong to a project - use standard flow breadcrumb path
    return {
      slugs: ['integrate', 'flows', String(rootNodeId)],
      breadcrumbList: [`${ROUTE_CONNECT}`, `${ROUTE_CONNECT}${ROUTE_PIPELINES}`, ''],
    };
  }, [rootNodeId, flowProjectId, entities.projects]);

  useEffect(() => {
    if (subHeaderConfig) {
      setSubHeader(subHeaderConfig);
      setCurrentSlug(rootNodeId);
    }
  }, [rootNodeId, loading, subHeaderConfig, setSubHeader, setCurrentSlug]);

  if (!rootNodeId) {
    return <ConnectionLoader loading message="Preparing the RAG Flow" />;
  }

  const onBack = () => {};

  return (
    <DraftContextProvider flowId={rootNodeId}>
      <Stack className={classes.container} flex={1} height="100%">
        <Stack className={classes.content}>
          <Header expanded onBack={onBack} backButton={false} handleToggleExpandedView={onBack} />
          <RAGFlowNodes rootNodeId={rootNodeId}>
            <EditFlowName flowId={rootNodeId} />
            <RagActions />
            <RAGAPIUsage />
          </RAGFlowNodes>
        </Stack>
        <ConnectionLoader loading={loading} message={'Loading flow'} />
      </Stack>
      {selectedCardID && <FlowDetailViewContainer />}
      <DraftConfirmationDialog />
    </DraftContextProvider>
  );
};

EditRAGFlow.screenConfig = {
  screenName: 'EditRAGFlow',
  domain: 'main',
  path: [
    `${ROUTE_CONNECT}${ROUTE_PIPELINES}/genai_rag/new`,
    `${ROUTE_CONNECT}${ROUTE_PIPELINES}/genai_rag/:id/edit`,
  ],
  pageTitle: 'RAG Flow',
} as ScreenConfig<any>;

export default EditRAGFlow;
